import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { TournamentNotice } from '@chess-uno/shared-protocol'
import type { StoredSession } from '../api/session.js'
import { getSocket } from '../api/socket.js'
import { playSound } from '../api/sound.js'
import { useI18n } from '../i18n/index.js'
import { roundLabel } from '../tournament-labels.js'

/**
 * A tournament match is ready, wherever the player is. On the tournament's own
 * page they are taken to the game at once (they are waiting for it); anywhere
 * else a notice offers to go, without pulling them out of what they are doing.
 */
export function TournamentToasts({ session }: { session: StoredSession }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const [notices, setNotices] = useState<TournamentNotice[]>([])
  const where = useRef(pathname)
  where.current = pathname

  useEffect(() => {
    const socket = getSocket(session)
    const onMatch = (n: TournamentNotice) => {
      playSound('join')
      if (where.current.toLowerCase() === `/tournament/${n.code.toLowerCase()}`) {
        navigate(`/game/${n.gameId}`)
        return
      }
      setNotices((list) => [...list.filter((x) => x.gameId !== n.gameId), n])
    }
    socket.on('tournament:match', onMatch)
    return () => {
      socket.off('tournament:match', onMatch)
    }
  }, [session, navigate])

  const dismiss = (gameId: string) => setNotices((list) => list.filter((x) => x.gameId !== gameId))
  if (notices.length === 0) return null
  return (
    <div className="toasts top" aria-live="polite">
      {notices.map((n) => (
        <div key={n.gameId} className="toast" role="alertdialog" aria-label={t.tournament.toast.aria(n.name || t.tournament.eyebrow)} data-testid="tournament-toast">
          <p>{t.tournament.toast.ready(n.opponentName)}</p>
          <p className="muted small">
            {n.name || t.tournament.eyebrow} · {roundLabel(n.round, n.rounds, t, true)}
          </p>
          <div className="toast-actions">
            <button
              type="button"
              data-testid="tournament-toast-play"
              onClick={() => {
                dismiss(n.gameId)
                navigate(`/game/${n.gameId}`)
              }}
            >
              {t.tournament.toast.play}
            </button>
            <button type="button" className="secondary" onClick={() => dismiss(n.gameId)}>
              {t.tournament.toast.later}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
